import { Ref } from "react";

/**
 * Will stop any further events
 * @param e - event that needs to be stopped
 */
export const stopEvent = (e: { stopPropagation: () => void; preventDefault: () => void }): void => {
	e.stopPropagation();
	e.preventDefault();
};

/**
 * Evaluates if a value is null or undefined
 * @param value - value to be evaluated
 * @returns true if value is null or undefined
 */
export function isNullOrUndefined<T>(value: T | undefined | null): value is undefined | null {
	return value === null || value === undefined;
}

/**
 * Will evaluate an event that appears to be a mouse event, to determine if it is infact a keyboard event
 * @param event - event to be evaluated
 * @returns true if event is not a mouse event, but rather a keyboard event
 */
export function isMouseEventKeyboard(e: React.MouseEvent | MouseEvent): boolean {
	//detail on mouse events, is the click count, if it is 0, it means it was not a mouse click.
	if ((e as MouseEvent).detail === 0) {
		return true;
	}
	const re = e as React.MouseEvent;
	if (re.nativeEvent && re.nativeEvent.detail === 0) {
		return true;
	}
	return false;
}

/**
 * Will generate a combined ref from multiple refs
 * @returns a combined ref
 */
export function combinedRefs<T>(...refs: Ref<T>[]): Ref<T> {
	return (element: T | null) =>
		refs.forEach((ref) => {
			if (!ref) {
				return;
			}

			// Ref can have two types - a function or an object. We treat each case.
			if (typeof ref === "function") {
				return ref(element);
			}

			// As per https://github.com/facebook/react/issues/13029
			// it should be fine to set current this way.
			(ref as { current: T | null }).current = element;
		});
}

/**
 * Takes an optional label and an optional id and returns one of them favoring label over id, and returns null if both are undefined
 * @param label - label to be used as "aria-label"
 * @param id - id of an element to be used for the "aria-labelledby" attribute
 * @returns an object with the property "aria-label" or "aria-labelledby" or null
 */
export function ariaLabelCombine(
	label: string | undefined | null,
	id: string | undefined | null
): Record<"aria-label", string> | Record<"aria-labelledby", string> | null {
	if (label) return { "aria-label": label };
	if (id) return { "aria-labelledby": id };
	return null;
}

/**
 * Checks if the provided value is not null or false
 * @param value to be checked
 * @returns boolean indicating whether the value is not null or false
 */
export function isNotNullOrFalse<T>(value: T | null | false): value is T {
	return value !== null && value !== false;
}

/**
 * Returns a data-observe-key value based on the provided baseKey and discriminator value(s).
 * The discriminator value(s) will be appended to the baseKey with a hyphen in between.
 * If the baseKey is undefined, undefined will be returned.
 * @param baseKey - base key to be used
 * @param discriminators - discriminator value(s) to be used
 * @returns a data-observe-key value
 */
export function dataObserveKeyDiscriminator(
	baseKey: string | undefined,
	...discriminators: string[]
): string | undefined {
	if (!baseKey) return undefined;
	if (Array.isArray(discriminators)) {
		return `${baseKey}-${discriminators.join("-")}`;
	}
	return `${baseKey}-${discriminators}`;
}
